import React from "react";
import DealStage from "@/model/interface/DealStage";
import Deal from "@/model/interface/Deal";
import { DealKanbanColumn } from "@/components/pages/deals/DealKanbanColumn";
import { Skeleton } from "@/components/ui/skeleton";
import { DealStageCategory, stageCategoryOf } from "@/enum/DealStageCategory";

interface DealKanbanBoardProps {
  stages: DealStage[];
  deals: Deal[];
  isLoading: boolean;
}

interface KanbanGroup {
  key: string;
  title: string;
  categories: DealStageCategory[];
}

const KANBAN_GROUPS: KanbanGroup[] = [
  { key: "lead", title: "Leads", categories: [DealStageCategory.LEAD] },
  { key: "pipeline", title: "Pipeline", categories: [DealStageCategory.PIPELINE] },
  { key: "closed", title: "Closed", categories: [DealStageCategory.WON, DealStageCategory.LOST] },
];

export function DealKanbanBoard({ stages, deals, isLoading }: DealKanbanBoardProps) {
  if (isLoading) {
    return (
      <div className="flex gap-4 h-full overflow-x-auto">
        {[1, 2, 3, 4].map((placeholder) => (
          <div key={placeholder} className="flex-shrink-0 w-72">
            <Skeleton className="h-8 w-full mb-3" />
            <Skeleton className="h-24 w-full mb-2" />
            <Skeleton className="h-24 w-full mb-2" />
          </div>
        ))}
      </div>
    );
  }

  const activeStages = stages.filter((stage) => stage.isActive);
  const groups = KANBAN_GROUPS.map((group) => ({
    ...group,
    stages: activeStages.filter((stage) => group.categories.includes(stageCategoryOf(stage))),
  })).filter((group) => group.stages.length > 0);

  return (
    <div className="flex gap-4 h-full overflow-x-auto pb-2">
      {groups.map((group, groupIndex) => (
        <React.Fragment key={group.key}>
          {group.key === "pipeline" && groupIndex > 0 && (
            <div
              className="flex shrink-0 flex-col items-center gap-2"
              title="A lead whose deal moves past this line becomes a contact"
            >
              <span className="text-xs font-medium uppercase tracking-wider text-info">Converts</span>
              <div className="flex-1 border-l-2 border-dashed border-info" />
            </div>
          )}
          <section className="flex min-h-0 flex-col gap-2">
            <span className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {group.title}
            </span>
            <div className="flex min-h-0 flex-1 gap-4">
              {group.stages.map((stage) => (
                <DealKanbanColumn
                  key={stage.id}
                  stage={stage}
                  deals={deals.filter((deal) => deal.stage?.id === stage.id)}
                />
              ))}
            </div>
          </section>
        </React.Fragment>
      ))}
    </div>
  );
}
